import { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserPlus,
  Briefcase,
  ArrowRightLeft,
  MessageSquare,
  Mail,
  CalendarCheck,
  AlertCircle,
  Bell,
  CheckCheck,
  Loader2,
} from 'lucide-react';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead } from '@/lib/api';
import type { Notification } from '@/types';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface NotificationDropdownProps {
  onClose: () => void;
}

function getNotificationIcon(type: string, category?: string) {
  switch (category || type) {
    case 'candidate_created':
    case 'candidate_updated':
      return <UserPlus className="h-4 w-4 text-emerald-400" />;
    case 'job_created':
    case 'job_updated':
      return <Briefcase className="h-4 w-4 text-blue-400" />;
    case 'stage_change':
    case 'application_created':
      return <ArrowRightLeft className="h-4 w-4 text-amber-400" />;
    case 'note_added':
    case 'mention':
      return <MessageSquare className="h-4 w-4 text-purple-400" />;
    case 'email_sent':
    case 'email_received':
      return <Mail className="h-4 w-4 text-cyan-400" />;
    case 'interview_scheduled':
    case 'interview':
      return <CalendarCheck className="h-4 w-4 text-orange-400" />;
    case 'invoice':
      return <Mail className="h-4 w-4 text-cyan-400" />;
    case 'submission':
      return <MessageSquare className="h-4 w-4 text-purple-400" />;
    case 'placement':
      return <Briefcase className="h-4 w-4 text-blue-400" />;
    case 'alert':
      return <AlertCircle className="h-4 w-4 text-red-400" />;
    default:
      return <Bell className="h-4 w-4 text-text-tertiary" />;
  }
}

function getEntityPath(entityType: string | null, entityId: string | null): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'candidate':
      return `/candidates/${entityId}`;
    case 'job':
      return `/jobs/${entityId}`;
    case 'application':
      return `/pipeline`;
    case 'company':
      return `/companies/${entityId}`;
    case 'invoice':
      return `/invoices/${entityId}`;
    case 'placement':
      return `/placements/${entityId}`;
    case 'interview':
      return '/interviews';
    case 'submission':
      return '/submissions';
    default:
      return null;
  }
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'dropdown'],
    queryFn: () => fetchNotifications({ page: 1, limit: 10 }),
  });

  const markReadMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  function handleNotificationClick(notification: Notification) {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    const path = getEntityPath(notification.entityType, notification.entityId);
    if (path) {
      navigate(path);
      onClose();
    }
  }

  const notifications = data?.data ?? [];
  const hasUnread = notifications.some((n) => !n.isRead);

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-96 bg-bg-elevated border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
            className="text-xs h-7 px-2"
          >
            {markAllReadMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <CheckCheck className="h-3 w-3 mr-1" />
            )}
            Mark all read
          </Button>
        )}
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full flex-shrink-0 bg-white/5" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3 w-3/4 bg-white/5" />
                  <Skeleton className="h-3 w-1/2 bg-white/5" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell className="h-8 w-8 text-text-tertiary mx-auto mb-2" />
            <p className="text-sm text-text-tertiary">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0 ${
                !notification.isRead ? 'bg-white/[0.02]' : ''
              }`}
            >
              <div className="flex-shrink-0 mt-0.5 h-8 w-8 rounded-full bg-white/5 flex items-center justify-center">
                {getNotificationIcon(notification.type, notification.category)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className={`text-sm leading-snug ${!notification.isRead ? 'font-medium text-text-primary' : 'text-text-secondary'}`}>
                    {notification.title}
                  </p>
                  {!notification.isRead && (
                    <span className="flex-shrink-0 h-2 w-2 rounded-full bg-emerald-500 mt-1.5" />
                  )}
                </div>
                <p className="text-xs text-text-tertiary mt-0.5 line-clamp-2">
                  {notification.message}
                </p>
                <p className="text-xs text-text-tertiary mt-1">
                  {formatRelativeTime(notification.createdAt)}
                </p>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 px-4 py-2">
        <Link
          to="/notifications"
          onClick={onClose}
          className="block text-center text-sm text-emerald-500 hover:text-emerald-400 py-1 transition-colors"
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}
